/**
 * Academic Content Validator
 *
 * Validates if uploaded files or pasted text contains legitimate academic content
 * related to allowed educational subjects, rather than garbage text or unrelated content.
 *
 * This validator uses AI-powered analysis to detect:
 * - Garbage/Lorem Ipsum text
 * - Random code snippets
 * - Irrelevant content (advertisements, recipes, etc.)
 * - Non-academic content
 *
 * Allowed subjects:
 * - Oral Communication
 * - Reading and Writing
 * - Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino
 * - Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik
 * - 21st Century Literature from the Philippines and the World
 * - General Mathematics
 * - Statistics and Probability
 * - Earth and Life Science
 * - Physical Science
 * - Earth Science
 */

import { generateGeminiText } from "@/services/geminiProxyService";
import { logDebug, logError, logInfo } from "./secureLogger";

export interface AcademicValidationResult {
  isValid: boolean;
  isAcademic: boolean;
  detectedContentType: string;
  matchedSubjects: string[];
  confidenceScore: number; // 0-100
  reasoning: string;
  warnings: string[];
  errors: string[];
}

export const ALLOWED_SUBJECTS = [
  "Oral Communication",
  "Reading and Writing",
  "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
  "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
  "21st Century Literature from the Philippines and the World",
  "General Mathematics",
  "Statistics and Probability",
  "Earth and Life Science",
  "Physical Science",
  "Earth Science",
];

/**
 * Validates if content is legitimate academic material
 * Uses AI to detect garbage text, random code, and irrelevant content
 *
 * @param content - The text content to validate
 * @param sessionSubject - Optional: The specific subject of the current session
 * @returns Validation result with detailed analysis
 */
export async function validateAcademicContent(
  content: string,
  sessionSubject?: string
): Promise<AcademicValidationResult> {
  try {
    logInfo("🎓 Starting academic content validation", {
      contentLength: content.length,
      sessionSubject,
      hasSessionSubject: !!sessionSubject,
    });

    // Basic pre-checks
    if (!content || content.trim().length < 50) {
      return {
        isValid: false,
        isAcademic: false,
        detectedContentType: "Too short",
        matchedSubjects: [],
        confidenceScore: 0,
        reasoning: "Content is too short to be valid academic material",
        warnings: [],
        errors: ["Content must be at least 50 characters"],
      };
    }

    // Call Gemini AI via secure backend proxy
    const prompt = `You are an expert academic content validator for a Filipino educational platform (Senior High School level).

ANALYZE the following text and determine if it is LEGITIMATE ACADEMIC CONTENT suitable for study materials.

ALLOWED ACADEMIC SUBJECTS (must match at least one):
${ALLOWED_SUBJECTS.map((subject, index) => `${index + 1}. ${subject}`).join("\n")}

${
  sessionSubject
    ? `\nCURRENT SESSION SUBJECT: ${sessionSubject}\n(The content should be relevant to this subject, but can also relate to other allowed subjects)`
    : ""
}

VALIDATION CRITERIA:

✅ ACCEPT if the content is:
- Educational notes, lessons, or study materials
- Academic explanations, definitions, or concepts
- Problem sets, exercises, or examples from allowed subjects
- Lecture notes or textbook excerpts from allowed subjects
- Research or essays on allowed subject topics
- Mixed content from multiple allowed subjects (interdisciplinary)

❌ REJECT if the content contains:
- Lorem Ipsum or placeholder text
- Random code snippets (JavaScript, Python, etc.) without educational context
- Non-educational content (recipes, advertisements, social media posts, etc.)
- Gibberish, random characters, or keyboard spam
- Copy-pasted text that clearly has nothing to do with education
- Entertainment content (movie scripts, song lyrics, game walkthroughs)
- Instructions for apps/software unrelated to education
- Test strings like "asdfasdf" or repeated characters

IMPORTANT SCORING GUIDELINES:
- Confidence Score: 0-100 (how certain you are about your classification)
  * 90-100: Definitely academic content from allowed subjects
  * 70-89: Likely academic, some unclear parts
  * 50-69: Questionable, mixed content
  * 30-49: Probably not academic
  * 0-29: Definitely not academic (garbage/spam)

- Content may contain minor typos or informal language (students' notes)
- Filipino and English mixed content is normal and acceptable
- Mathematical formulas, scientific notation, and technical terms are positive indicators
- Cross-subject content (e.g., Math in Physics) is acceptable if both subjects are allowed

CONTENT TO ANALYZE:
"""
${content.substring(0, 3000)}${content.length > 3000 ? "\n...(content truncated for analysis)" : ""}
"""

Respond in JSON format:
{
  "isAcademic": true/false (is this legitimate academic content?),
  "detectedContentType": "string describing what type of content this is",
  "matchedSubjects": ["array", "of", "matched", "allowed", "subjects"],
  "confidenceScore": 0-100,
  "reasoning": "Clear explanation of your decision",
  "warnings": ["array of any concerns even if accepting"],
  "specificIssues": ["array of specific problems found, empty if none"]
}

Be strict but fair. Student notes can be messy, but should still be recognizable as educational content.`;

    const geminiResult = await generateGeminiText({
      prompt,
      model: "gemini-2.5-flash",
      temperature: 0.3,
    });

    if (!geminiResult.success || !geminiResult.text) {
      logError("Academic validation API call failed", geminiResult.error);
      return {
        isValid: false,
        isAcademic: false,
        detectedContentType: "Unknown",
        matchedSubjects: [],
        confidenceScore: 0,
        reasoning: "Unable to validate content - API error",
        warnings: ["AI validation failed"],
        errors: [geminiResult.error || "Unknown error"],
      };
    }

    const text = geminiResult.text;

    logDebug("Gemini academic validation response", {
      rawResponse: text.substring(0, 500),
    });

    // Parse JSON response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      logError("Failed to parse academic validation response - no JSON found");
      return {
        isValid: false,
        isAcademic: false,
        detectedContentType: "Unknown",
        matchedSubjects: [],
        confidenceScore: 0,
        reasoning: "Unable to validate content - parsing error",
        warnings: ["AI validation failed"],
        errors: ["Could not analyze content"],
      };
    }

    const analysis = JSON.parse(jsonMatch[0]);

    // Determine if content is valid
    // Valid means: isAcademic AND (confidenceScore >= 70 OR has matched subjects)
    const hasMatchedSubjects = analysis.matchedSubjects && analysis.matchedSubjects.length > 0;
    const isHighConfidence = analysis.confidenceScore >= 70;
    const isValid = analysis.isAcademic && (isHighConfidence || hasMatchedSubjects);

    // Additional validation: If session subject is provided, check if it's in matched subjects
    let sessionSubjectWarning = "";
    if (sessionSubject && hasMatchedSubjects) {
      const sessionSubjectMatched = analysis.matchedSubjects.some(
        (subject: string) =>
          subject.toLowerCase().includes(sessionSubject.toLowerCase()) ||
          sessionSubject.toLowerCase().includes(subject.toLowerCase())
      );

      if (!sessionSubjectMatched) {
        sessionSubjectWarning = `Content appears to be about ${analysis.matchedSubjects.join(
          ", "
        )} but current session is ${sessionSubject}`;
      }
    }

    const warnings = [
      ...(analysis.warnings || []),
      ...(sessionSubjectWarning ? [sessionSubjectWarning] : []),
    ];

    const validationResult: AcademicValidationResult = {
      isValid,
      isAcademic: analysis.isAcademic,
      detectedContentType: analysis.detectedContentType,
      matchedSubjects: analysis.matchedSubjects || [],
      confidenceScore: analysis.confidenceScore,
      reasoning: analysis.reasoning,
      warnings,
      errors: analysis.specificIssues || [],
    };

    logInfo("🎓 Academic validation completed", {
      isValid,
      isAcademic: analysis.isAcademic,
      detectedContentType: analysis.detectedContentType,
      matchedSubjects: analysis.matchedSubjects,
      confidenceScore: analysis.confidenceScore,
      hasWarnings: warnings.length > 0,
      hasErrors: validationResult.errors.length > 0,
    });

    return validationResult;
  } catch (error) {
    logError("Error validating academic content:", error);

    // On error, return permissive result to avoid blocking valid content
    return {
      isValid: true,
      isAcademic: true,
      detectedContentType: "Unknown (validation error)",
      matchedSubjects: [],
      confidenceScore: 50,
      reasoning: "Validation system error - proceeding with caution",
      warnings: ["Unable to fully validate content due to system error"],
      errors: [],
    };
  }
}

/**
 * Generate user-friendly error message from validation result
 */
export function getValidationErrorMessage(result: AcademicValidationResult): string {
  if (result.isValid) {
    return "";
  }

  let message = "⚠️ Content Validation Failed\n\n";

  if (!result.isAcademic) {
    message += `The uploaded content appears to be "${result.detectedContentType}" rather than academic study material.\n\n`;
    message += `${result.reasoning}\n\n`;
  } else if (result.confidenceScore < 70) {
    message += `The content quality is questionable (confidence: ${result.confidenceScore}%).\n\n`;
    message += `${result.reasoning}\n\n`;
  }

  if (result.errors.length > 0) {
    message += "Specific Issues:\n";
    result.errors.forEach((error) => {
      message += `• ${error}\n`;
    });
    message += "\n";
  }

  message += "✅ Allowed Content:\n";
  message += "Please upload study notes, lessons, or educational materials related to:\n";
  ALLOWED_SUBJECTS.forEach((subject) => {
    message += `• ${subject}\n`;
  });

  return message;
}

/**
 * Quick validation check with user-friendly error handling
 * Returns true if valid, shows error alert and returns false if invalid
 */
export async function validateAndShowError(
  content: string,
  sessionSubject?: string
): Promise<boolean> {
  const result = await validateAcademicContent(content, sessionSubject);

  if (!result.isValid) {
    // Will be handled by calling component with SweetAlert
    return false;
  }

  // Show warnings if present but still valid
  if (result.warnings.length > 0) {
    logInfo("Academic validation passed with warnings", {
      warnings: result.warnings,
    });
  }

  return true;
}
